import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getUserData, registerUser, resendOtp, verifyOtp } from "../../API/API";
import {
  showToast,
  TOKEN_KEYS,
  ensureAuthTokens,
  autoLogin,
} from "../../components/common/Util/Util";
import OrganizationVerification from "../../components/forms/LenderOnboarding/OrganizationVerification";
import { setUser } from "../../ReduxStore/UserSlice";
import { openDashboardInNewTab } from "../../utils/navigationUtils";

const OTPVerification = ({
  showOTPVarification,
  setShowOTPVarification,
  showMobileOTP,
  setShowWelcome,
  setRequestData,
  requestData,
  redirectToApplyLoan,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [otpError, setOtpError] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [lenderId, setLenderId] = useState(null);

  const [otp, setOtp] = useState({
    mobile: ["", "", "", ""],
    email: ["", "", "", ""],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [timers, setTimers] = useState({
    mobile: 60,
  });

  const [resendVisible, setResendVisible] = useState({
    mobile: false,
  });

  // Handle countdown timers
  useEffect(() => {
    const intervals = {};

    if (timers.mobile > 0) {
      intervals.mobile = setInterval(() => {
        setTimers((prev) => ({ ...prev, mobile: prev.mobile - 1 }));
      }, 1000);
    } else {
      setResendVisible((prev) => ({ ...prev, mobile: true }));
    }

    return () => {
      if (intervals.mobile) clearInterval(intervals.mobile);
    };
  }, [timers.mobile]);

  const handleChange = (e, index, type) => {
    const value = e.target.value;
    if (value.length <= 1 && !isNaN(value)) {
      setOtp((prev) => {
        const newOtp = { ...prev };
        newOtp[type][index] = value;
        return newOtp;
      });

      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`${type}-otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleClose = () => {
    setShowOTPVarification(false);
  };

  const handleLenderVerification = async (userData, jwtToken) => {
    try {
      // Store temporary tokens
      const originalToken = localStorage.getItem("jwtToken");

      // Set the new token for the API call
      localStorage.setItem("jwtToken", jwtToken);

      try {
        // Check verification status
        const verificationResponse = await getUserData(userData.UUID);
        // Check if the lender is verified
        if (verificationResponse?.result?.lender?.verified) {
          // If verified, proceed with normal login
          const completeUserData = {
            ...userData,
            lender: verificationResponse.result.lender,
          };
          dispatch(setUser(completeUserData));
          localStorage.setItem("jwtToken", jwtToken);
          localStorage.setItem("user", JSON.stringify(completeUserData));
          localStorage.setItem("userType", "lender");

          // Save lenderId in localStorage from the lender object
          if (verificationResponse?.result?.lender?.lenderId) {
            localStorage.setItem(
              "lenderId",
              verificationResponse.result.lender.lenderId
            );
          } else {
            console.error(
              "LenderId not found in response:",
              verificationResponse
            );
          }

          // Remove any temporary tokens
          localStorage.removeItem("tempJwtToken");
          localStorage.removeItem("tempUuid");
          autoLogin(completeUserData, dispatch);
          handleClose();
          if (redirectToApplyLoan) {
            window.open("/apply-loan", "_blank");
          } else {
            openDashboardInNewTab();
          }
        } else {
          // If not verified, store tokens for verification process
          localStorage.setItem("tempJwtToken", jwtToken);
          localStorage.setItem("tempUuid", userData.UUID);
          // Restore original token if it existed
          if (originalToken) {
            localStorage.setItem("jwtToken", originalToken);
          } else {
            localStorage.removeItem("jwtToken");
          }
          // Show verification screen
          const lenderIdToShow =
            verificationResponse?.result?.lender?.lenderId || userData?.ID;
          setLenderId(lenderIdToShow);
          setShowVerification(true);
        }
      } catch (error) {
        // Restore original token in case of error
        if (originalToken) {
          localStorage.setItem("jwtToken", originalToken);
        } else {
          localStorage.removeItem("jwtToken");
        }
        throw error;
      }
    } catch (error) {
      console.error("Error checking lender verification:", error);
      showToast("Error checking verification status", "error");
    }
  };

  // const handleVerificationSuccess = () => {
  //   setShowVerification(false);
  //   handleClose();
  // };

  const handleUserLogin = (userData, jwtToken) => {
    try {
      const completeUserData = {
        ...userData,
        jwtToken,
        isLoggedIn: true,
      };

      if (autoLogin(completeUserData, dispatch)) {
        localStorage.setItem(
          TOKEN_KEYS.USER_INFO,
          JSON.stringify(completeUserData)
        );

        ensureAuthTokens(completeUserData);

        if (userData.sessionId) {
          localStorage.setItem("sessionId", userData.sessionId);
        }
        sessionStorage.removeItem("registrationData");
        handleClose();
        if (userData?.userType||userData?.UserType) {
          window.open(
            redirectToApplyLoan ? "/apply-loan" : "/dashboard",
            "_blank"
          );
        }
      }
    } catch (error) {
      console.error("Error in handleUserLogin:", error);
      showToast("Login failed. Please try again.", "error");
    }
  };

  const onSubmit = () => {
    if (isSubmitting) {
      return;
    }

    const enteredMobileOtp = otp.mobile.join("");
    const enteredEmailOtp = otp.email.join("");

    if (!enteredMobileOtp || enteredMobileOtp.length < 4) {
      setOtpError(true);
      showToast("Please enter the complete Mobile OTP", "error");
      return;
    }

    if (!showMobileOTP && (!enteredEmailOtp || enteredEmailOtp.length < 4)) {
      setOtpError(true);
      showToast("Please enter the complete Email OTP", "error");
      return;
    }

    if (!requestData) {
      showToast(
        "Registration data not found. Please try registering again.",
        "error"
      );
      return;
    }

    setOtpError(false);
    setIsSubmitting(true);

    const verifyOtpRequest = {
      email: requestData.email,
      emailOtp: enteredEmailOtp,
      mobileNumber: requestData.mobileNumber,
      mobileOtp: enteredMobileOtp,
    };

    if (showMobileOTP) {
      verifyOtp(verifyOtpRequest)
        .then((res) => {
          console.log("OTP Verification Response:", res);
          if (res?.statusCode === 200) {
            showToast("Logged in successfully", "success");

            // Check if user is a lender (handle both uppercase and lowercase)
            const userType = res.result.UserType || res.result.userType;
            console.log("User Type:", userType);
            if (userType?.toLowerCase() === "lender") {
              handleLenderVerification(res.result, res.result.jwtToken);
            } else {
              handleUserLogin(res?.result, res?.result?.jwtToken);
            }
          } else {
            showToast(res.message, "error");
          }
        })
        .catch((e) => {
          showToast("Oops! Something went wrong.", "error");
          console.error(e);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      verifyOtp(verifyOtpRequest)
        .then((res) => {
          if (res?.statusCode === 200) {
            showToast(res.message, "success");
            registerUser(requestData).then((registerRes) => {
              if (registerRes?.statusCode === 200) {
                showToast("Registration successful", "success");

                dispatch(setUser(registerRes.result));
                localStorage.setItem("jwtToken", registerRes.result.jwtToken);
                handleClose();
              } else {
                showToast(
                  registerRes.message || "Registration failed",
                  "error"
                );
              }
            });
            setRequestData(requestData);
            handleClose();
          } else {
            showToast(res.message, "error");
          }
        })
        .catch((e) => {
          showToast("Oops! Something went wrong.", "error");
          console.error(e);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleResend = () => {
    if (!requestData) {
      showToast(
        "Registration data not found. Please try registering again.",
        "error"
      );
      return;
    }

    const payload = {
      ip: requestData.ip,
      longitude: requestData.longitude,
      latitude: requestData.latitude,
      screen: requestData.screen,
      ...(!showMobileOTP && { email: requestData.email }),
      mobileNumber: requestData.mobileNumber,
    };

    resendOtp(payload)
      .then((res) => {
        if (res?.statusCode === 200) {
          showToast("OTP sent successfully", "success");
          // Clear OTP fields
          setOtp({
            mobile: ["", "", "", ""],
            email: ["", "", "", ""],
          });
          setTimers((prev) => ({ ...prev, mobile: 60 }));
          setResendVisible((prev) => ({ ...prev, mobile: false }));
        } else {
          showToast(res.message || "Failed to send OTP", "error");
        }
      })
      .catch((e) => {
        showToast(e.message || "Failed to send OTP", "error");
        console.error(e);
      });
  };

  if (showVerification) {
    return (
      <Dialog
        open={showVerification}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogContent>
          <OrganizationVerification
            formData={requestData}
            initialData={requestData}
            lenderId={lenderId}
            preventClose={true}
            onVerificationSuccess={() => {
              setShowVerification(false);
              handleClose();
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={showOTPVarification}
      onClose={handleClose}
      maxWidth={showMobileOTP ? "md" : "lg"}
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ padding: 0, position: "relative" }}>
        <CloseIcon
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            cursor: "pointer",
            zIndex: 2,
          }}
        />
        <Grid container sx={{ height: "100%" }}>
          {/* Left Side Image (Merged with Border) */}
          <Grid
            item
            xs={12}
            sm={5}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `url(/fintech.png) center/cover no-repeat`,
            }}
          />

          {/* Right Side Form */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "2rem",
              position: "relative",
              [theme.breakpoints.down("sm")]: { padding: "1rem" },
            }}
          >
            {/* Logo at Top Center */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img src="/dequiLogo.png" alt="Logo" style={{ width: "150px" }} />
            </Box>

            <Typography
              variant="h6"
              sx={{ color: "#003A91", fontWeight: 700, mb: 1 }}
            >
              OTP Verification
            </Typography>

            <Typography
              sx={{
                color: "#333333",
                fontSize: "0.9rem",
                fontWeight: 400,
                mb: 2,
                fontStyle: "italic",
              }}
            >
              Enter the verification code sent via SMS on your{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Mobile Number
              </Box>
              .
            </Typography>

            {/* OTP Input fields */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              {otp.mobile.map((digit, index) => (
                <TextField
                  key={index}
                  id={`mobile-otp-${index}`}
                  error={otpError && digit === ""}
                  variant="outlined"
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      width: "50px",
                    },
                  }}
                  value={digit}
                  onChange={(e) => handleChange(e, index, "mobile")}
                />
              ))}
            </Box>

            {/* Resend Option */}
            {showMobileOTP && (
              <>
                {resendVisible.mobile ? (
                  <Typography sx={{ fontSize: "0.8rem", mb: 2 }}>
                    Didn't receive OTP?{" "}
                    <Button onClick={() => handleResend()} size="small">
                      Resend OTP
                    </Button>
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "0.8rem", mb: 2 }}>
                    Resend OTP in {timers.mobile} sec
                  </Typography>
                )}
              </>
            )}
            {!showMobileOTP && (
              <>
                <Typography
                  sx={{
                    color: "#333333",
                    fontSize: "0.8rem",
                    fontWeight: 200,
                    marginBottom: "1rem",
                    fontStyle: "italic",
                    marginTop: "0.5rem",
                  }}
                >
                  Enter the verification code we just sent on your{" "}
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    email ID
                  </Box>
                  .
                </Typography>
                <Box sx={{ display: "flex", gap: 3, marginBottom: "0.6rem" }}>
                  {otp.email.map((digit, index) => (
                    <TextField
                      key={index}
                      id={`email-otp-${index}`}
                      variant="outlined"
                      error={otpError && digit === ""}
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: "center",
                          fontSize: "1.5rem",
                          width: "40px",
                        },
                      }}
                      value={digit}
                      onChange={(e) => handleChange(e, index, "email")}
                    />
                  ))}
                </Box>
                {/* Resend Option */}
                {resendVisible.mobile ? (
                  <Typography sx={{ fontSize: "0.8rem", mb: 2 }}>
                    Didn't receive OTP?{" "}
                    <Button onClick={() => handleResend()} size="small">
                      Resend OTP
                    </Button>
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "0.8rem", mb: 2 }}>
                    Resend OTP in {timers.mobile} sec
                  </Typography>
                )}
              </>
            )}
            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{
                mt: 3,
                backgroundColor: "#003A91",
                borderRadius: "8px",
                mb: 2,
                "&:disabled": {
                  backgroundColor: "#ccc",
                  color: "#666",
                },
              }}
              onClick={(e) => {
                if (e.detail !== 0) onSubmit();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit();
              }}
            >
              {isSubmitting ? "Verifying..." : "Verify"}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default OTPVerification;
